import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  addDoc,
  serverTimestamp,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { writeAuditLog } from './auditLog'
import { AssignmentStatus, MessageType, TeamStatus } from '@/types'
import type { Assignment, TeamAssignment } from '@/types'

export const useAssignmentsStore = defineStore('assignments', () => {
  const assignments = ref<Assignment[]>([])
  const teamAssignments = ref<TeamAssignment[]>([])

  let assignmentsUnsubscribe: Unsubscribe | null = null
  let taUnsubscribe: Unsubscribe | null = null

  let _incidentId = ''
  let _opId = ''

  function subscribeToAssignments(incidentId: string, opId: string) {
    // Multiple views call this concurrently; skip if already on the same path to avoid a Mobile Safari Firebase crash (r.Iu.get).
    if (incidentId === _incidentId && opId === _opId && assignmentsUnsubscribe) return

    if (assignmentsUnsubscribe) assignmentsUnsubscribe()
    if (taUnsubscribe) taUnsubscribe()

    _incidentId = incidentId
    _opId = opId

    assignmentsUnsubscribe = onSnapshot(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments'),
      (snap) => {
        assignments.value = snap.docs.map(
          (d) => ({ assignment_id: d.id, ...d.data() }) as Assignment,
        )
      },
    )

    taUnsubscribe = onSnapshot(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'team_assignments'),
      (snap) => {
        teamAssignments.value = snap.docs.map(
          (d) => ({ ta_id: d.id, ...d.data() }) as TeamAssignment,
        )
      },
    )
  }

  async function createAssignment(
    incidentId: string,
    opId: string,
    data: {
      assignment_name: string
      description?: string
      prev_search_efforts?: string
      time_allocated?: string
      size_of_assignment?: string
    },
  ) {
    const docRef = await addDoc(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments'),
      {
        op_id: opId,
        assignment_name: data.assignment_name,
        description: data.description ?? '',
        assignment_status: AssignmentStatus.Draft,
        prev_search_efforts: data.prev_search_efforts ?? '',
        time_allocated: data.time_allocated ?? '',
        size_of_assignment: data.size_of_assignment ?? '',
        created_at: serverTimestamp(),
      },
    )

    await writeAuditLog(incidentId, 'assignment.created', 'assignment', docRef.id, null, {
      assignment_name: data.assignment_name,
    })

    return docRef.id
  }

  /**
   * Assign a team to an assignment. Supports both primary and secondary (concurrent) assignments.
   * - Assignment status is NOT changed — it stays NotStarted until the team calls in via comms
   *   or an overhead user manually advances it.
   * - Sets team status to Assigned regardless of current assignment status.
   * - Rejects if assignment is Completed.
   */
  async function assignTeam(
    incidentId: string,
    opId: string,
    assignmentId: string,
    teamId: string,
  ) {
    const assignment = assignments.value.find((a) => a.assignment_id === assignmentId)
    console.log('[Assignments] assignTeam — assignmentId:', assignmentId, 'teamId:', teamId, 'status:', assignment?.assignment_status)

    if (assignment?.assignment_status === AssignmentStatus.Completed) {
      throw new Error('Cannot assign a team to a completed assignment.')
    }
    if (assignment?.assignment_status === AssignmentStatus.Draft) {
      throw new Error('Cannot assign a team to a draft assignment.')
    }

    await addDoc(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'team_assignments'),
      {
        assignment_id: assignmentId,
        team_id: teamId,
        assigned_at: serverTimestamp(),
        debrief_cols: {},
      },
    )

    // Set team status to Assigned
    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'teams', teamId),
      { team_status: TeamStatus.Assigned },
    )

    await writeAuditLog(
      incidentId,
      'team.assigned',
      'assignment',
      assignmentId,
      { assignment_status: assignment?.assignment_status ?? null },
      { assignment_status: assignment?.assignment_status ?? null, team_id: teamId },
    )
  }

  /** Detach a team from an assignment. Reverts team to AwaitingAssignment if no other assignments remain. */
  async function detachTeam(
    incidentId: string,
    opId: string,
    assignmentId: string,
    teamId: string,
  ) {
    const assignment = assignments.value.find((a) => a.assignment_id === assignmentId)
    console.log('[Assignments] detachTeam — assignmentId:', assignmentId, 'teamId:', teamId, 'status:', assignment?.assignment_status)
    if (assignment?.assignment_status === AssignmentStatus.Completed) {
      throw new Error('Cannot remove a team from a completed assignment.')
    }

    const ta = teamAssignments.value.find(
      (r) => r.assignment_id === assignmentId && r.team_id === teamId,
    )
    if (!ta) return

    await deleteDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'team_assignments', ta.ta_id),
    )

    // If team has no remaining assignments, revert team to AwaitingAssignment
    const teamStillAssigned = teamAssignments.value.some(
      (r) => r.team_id === teamId && r.ta_id !== ta.ta_id,
    )
    if (!teamStillAssigned) {
      await updateDoc(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'teams', teamId),
        { team_status: TeamStatus.AwaitingAssignment },
      )
    }

    // If assignment has no remaining teams and was InProgress, revert to NotStarted.
    // A Completed assignment stays Completed regardless of team removal.
    const assignmentStillHasTeam = teamAssignments.value.some(
      (r) => r.assignment_id === assignmentId && r.ta_id !== ta.ta_id,
    )
    const currentStatus = assignments.value.find((a) => a.assignment_id === assignmentId)?.assignment_status
    if (!assignmentStillHasTeam && currentStatus === AssignmentStatus.InProgress) {
      await updateDoc(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments', assignmentId),
        { assignment_status: AssignmentStatus.NotStarted },
      )
    }

    await writeAuditLog(
      incidentId,
      'team.detached',
      'assignment',
      assignmentId,
      { team_id: teamId },
      null,
    )
  }

  /** Update app-managed fields on an assignment. */
  async function updateAssignment(
    incidentId: string,
    opId: string,
    assignmentId: string,
    fields: Partial<Assignment>,
  ) {
    console.log('[Assignments] updateAssignment — assignmentId:', assignmentId, 'fields:', fields)
    const before = assignments.value.find((a) => a.assignment_id === assignmentId)
    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments', assignmentId),
      fields,
    )
    await writeAuditLog(
      incidentId,
      'assignment.updated',
      'assignment',
      assignmentId,
      before ? Object.fromEntries(Object.keys(fields).map((k) => [k, (before as Record<string, unknown>)[k]])) : null,
      fields as Record<string, unknown>,
    )
  }

  /** Set assignment to any status directly, without transition guards. */
  async function setAssignmentStatus(incidentId: string, opId: string, assignmentId: string, status: AssignmentStatus) {
    const assignment = assignments.value.find((a) => a.assignment_id === assignmentId)
    if (!assignment) return
    const before = assignment.assignment_status
    console.log('[Assignments] setAssignmentStatus — assignmentId:', assignmentId, before, '→', status)
    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments', assignmentId),
      { assignment_status: status },
    )
    await writeAuditLog(
      incidentId,
      'assignment.status_changed',
      'assignment',
      assignmentId,
      { assignment_status: before },
      { assignment_status: status, source: 'manual' },
    )
  }

  /** Mark an assignment as Completed without requiring debrief data. */
  async function markAssignmentComplete(incidentId: string, opId: string, assignmentId: string) {
    console.log('[Assignments] markAssignmentComplete — assignmentId:', assignmentId)
    const assignment = assignments.value.find((a) => a.assignment_id === assignmentId)

    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments', assignmentId),
      { assignment_status: AssignmentStatus.Completed },
    )

    // Stamp completed_at on all team_assignments for this assignment
    const tas = teamAssignments.value.filter((ta) => ta.assignment_id === assignmentId)
    await Promise.all(
      tas.map((ta) =>
        updateDoc(
          doc(db, 'incidents', incidentId, 'operational_periods', opId, 'team_assignments', ta.ta_id),
          { completed_at: serverTimestamp() },
        ),
      ),
    )

    await writeAuditLog(
      incidentId,
      'assignment.status_changed',
      'assignment',
      assignmentId,
      { assignment_status: assignment?.assignment_status ?? null },
      { assignment_status: AssignmentStatus.Completed },
    )
  }

  /** Advance a Draft assignment to NotStarted, making it ready for team assignment. */
  async function markAssignmentNotStarted(incidentId: string, opId: string, assignmentId: string) {
    const assignment = assignments.value.find((a) => a.assignment_id === assignmentId)
    if (!assignment) return
    console.log('[Assignments] markAssignmentNotStarted — assignmentId:', assignmentId, 'current status:', assignment.assignment_status)
    if (assignment.assignment_status !== AssignmentStatus.Draft) return

    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments', assignmentId),
      { assignment_status: AssignmentStatus.NotStarted },
    )

    await writeAuditLog(
      incidentId,
      'assignment.status_changed',
      'assignment',
      assignmentId,
      { assignment_status: AssignmentStatus.Draft },
      { assignment_status: AssignmentStatus.NotStarted },
    )
  }

  /**
   * Manually advance an assignment from NotStarted → InProgress.
   * Sets all assigned teams to OnScene. Used when overhead starts an assignment
   * without a comms log entry (e.g. comms missed the call-in).
   */
  async function markAssignmentInProgress(incidentId: string, opId: string, assignmentId: string) {
    const assignment = assignments.value.find((a) => a.assignment_id === assignmentId)
    if (!assignment) return
    console.log('[Assignments] markAssignmentInProgress — assignmentId:', assignmentId, 'current status:', assignment.assignment_status)
    if (assignment.assignment_status !== AssignmentStatus.NotStarted) return

    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments', assignmentId),
      { assignment_status: AssignmentStatus.InProgress },
    )

    // Stamp started_at on all team_assignments for this assignment
    const tas = teamAssignments.value.filter((ta) => ta.assignment_id === assignmentId)
    await Promise.all(
      tas.map((ta) =>
        updateDoc(
          doc(db, 'incidents', incidentId, 'operational_periods', opId, 'team_assignments', ta.ta_id),
          { started_at: serverTimestamp() },
        ),
      ),
    )

    const teamIds = getAssignedTeamIds(assignmentId)
    await Promise.all(
      teamIds.map((teamId) =>
        updateDoc(
          doc(db, 'incidents', incidentId, 'operational_periods', opId, 'teams', teamId),
          { team_status: TeamStatus.OnScene },
        ),
      ),
    )

    await writeAuditLog(
      incidentId,
      'assignment.status_changed',
      'assignment',
      assignmentId,
      { assignment_status: AssignmentStatus.NotStarted },
      { assignment_status: AssignmentStatus.InProgress, source: 'manual' },
    )
  }

  /**
   * Reopen a Completed assignment, reverting it to InProgress.
   * Used when an overhead user needs to correct a premature completion.
   */
  async function reopenAssignment(incidentId: string, opId: string, assignmentId: string) {
    const assignment = assignments.value.find((a) => a.assignment_id === assignmentId)
    if (!assignment) return
    console.log('[Assignments] reopenAssignment — assignmentId:', assignmentId)
    if (assignment.assignment_status !== AssignmentStatus.Completed) return

    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments', assignmentId),
      { assignment_status: AssignmentStatus.InProgress },
    )

    await writeAuditLog(
      incidentId,
      'assignment.status_changed',
      'assignment',
      assignmentId,
      { assignment_status: AssignmentStatus.Completed },
      { assignment_status: AssignmentStatus.InProgress, source: 'reopen' },
    )
  }

  /**
   * Apply an assignment status transition triggered by a comms log entry.
   * Called after a radio log entry is committed for StartingAssignment or
   * CompletedAssignment message types. All transitions are idempotent.
   *
   * Departing:
   *   - Sets team status to Enroute
   *   - No-op if teamId is null
   *
   * RequestingAssignment:
   *   - Sets team status to AwaitingAssignment
   *   - No-op if teamId is null
   *
   * StartingAssignment:
   *   - Creates team_assignment link if not already present
   *   - Advances assignment NotStarted → InProgress
   *   - Sets team status to OnScene
   *   - No-op if assignment is Completed or teamId is null
   *
   * CompletedAssignment:
   *   - Sets assignment to Completed if not already
   */
  async function applyCommsTransition(
    incidentId: string,
    opId: string,
    messageType: MessageType,
    assignmentId: string | null,
    teamId: string | null,
  ): Promise<void> {
    console.log('[Assignments] applyCommsTransition — messageType:', messageType, 'assignmentId:', assignmentId, 'teamId:', teamId)

    if (messageType === MessageType.Enroute) {
      if (!teamId) return
      await updateDoc(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'teams', teamId),
        { team_status: TeamStatus.Enroute },
      )
      await writeAuditLog(
        incidentId,
        'team.status_changed',
        'team',
        teamId,
        null,
        { team_status: TeamStatus.Enroute, source: 'comms' },
      )
      return
    }

    if (messageType === MessageType.RequestingAssignment) {
      if (!teamId) return
      await updateDoc(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'teams', teamId),
        { team_status: TeamStatus.AwaitingAssignment },
      )
      await writeAuditLog(
        incidentId,
        'team.status_changed',
        'team',
        teamId,
        null,
        { team_status: TeamStatus.AwaitingAssignment, source: 'comms' },
      )
      return
    }

    if (!assignmentId) return
    const assignment = assignments.value.find((a) => a.assignment_id === assignmentId)
    if (!assignment) return

    if (messageType === MessageType.StartingAssignment) {
      if (assignment.assignment_status === AssignmentStatus.Completed) return
      if (!teamId) return

      const existingTa = teamAssignments.value.find(
        (ta) => ta.assignment_id === assignmentId && ta.team_id === teamId,
      )
      if (!existingTa) {
        await addDoc(
          collection(db, 'incidents', incidentId, 'operational_periods', opId, 'team_assignments'),
          { assignment_id: assignmentId, team_id: teamId, assigned_at: serverTimestamp(), started_at: serverTimestamp(), completed_at: null, debrief_cols: {} },
        )
      } else {
        await updateDoc(
          doc(db, 'incidents', incidentId, 'operational_periods', opId, 'team_assignments', existingTa.ta_id),
          { started_at: serverTimestamp() },
        )
      }

      if (assignment.assignment_status === AssignmentStatus.NotStarted) {
        await updateDoc(
          doc(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments', assignmentId),
          { assignment_status: AssignmentStatus.InProgress },
        )
      }

      await updateDoc(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'teams', teamId),
        { team_status: TeamStatus.OnScene },
      )

      await writeAuditLog(
        incidentId,
        'team.assigned',
        'assignment',
        assignmentId,
        { assignment_status: assignment.assignment_status },
        { assignment_status: AssignmentStatus.InProgress, team_id: teamId, team_status: TeamStatus.OnScene, source: 'comms' },
      )
    } else if (messageType === MessageType.CompletedAssignment) {
      if (assignment.assignment_status === AssignmentStatus.Completed) return
      await markAssignmentComplete(incidentId, opId, assignmentId)
    }
  }

  /** Submit debrief data for a specific team_assignment record. */
  async function submitDebrief(
    incidentId: string,
    opId: string,
    taId: string,
    debriefData: Record<string, unknown>,
  ) {
    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'team_assignments', taId),
      { debrief_cols: debriefData },
    )

    await writeAuditLog(
      incidentId,
      'assignment.debrief_submitted',
      'team_assignment',
      taId,
      null,
      debriefData,
    )
  }

  /** Batch-write sort_order for a manually reordered list of assignments. */
  async function reorderAssignments(incidentId: string, opId: string, orderedAssignments: Assignment[]) {
    const batch = writeBatch(db)
    orderedAssignments.forEach((assignment, index) => {
      batch.update(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'assignments', assignment.assignment_id),
        { sort_order: index + 1 },
      )
    })
    await batch.commit()
    await writeAuditLog(incidentId, 'assignment.reorder', 'assignment', 'batch', null, {
      order: orderedAssignments.map((a) => a.assignment_id),
    })
  }

  /** Returns all team_ids currently assigned to an assignment. */
  function getAssignedTeamIds(assignmentId: string): string[] {
    return teamAssignments.value
      .filter((ta) => ta.assignment_id === assignmentId)
      .map((ta) => ta.team_id)
  }

  /** Returns the first team_id assigned to an assignment, or null (kept for backward compat). */
  function getAssignedTeamId(assignmentId: string): string | null {
    return teamAssignments.value.find((ta) => ta.assignment_id === assignmentId)?.team_id ?? null
  }

  /** Returns all team_assignment records for a given team in the current OP. */
  function getTeamAssignments(teamId: string): TeamAssignment[] {
    return teamAssignments.value.filter((ta) => ta.team_id === teamId)
  }

  function teardown() {
    if (assignmentsUnsubscribe) assignmentsUnsubscribe()
    if (taUnsubscribe) taUnsubscribe()
    assignmentsUnsubscribe = null
    taUnsubscribe = null
    assignments.value = []
    teamAssignments.value = []
  }

  return {
    assignments,
    teamAssignments,
    subscribeToAssignments,
    createAssignment,
    assignTeam,
    detachTeam,
    updateAssignment,
    setAssignmentStatus,
    markAssignmentNotStarted,
    markAssignmentInProgress,
    markAssignmentComplete,
    reopenAssignment,
    applyCommsTransition,
    submitDebrief,
    reorderAssignments,
    getAssignedTeamIds,
    getAssignedTeamId,
    getTeamAssignments,
    teardown,
  }
})
